import { ArrowDown2, Colorfilter, Slash } from '@/components/icons';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { resolveDividerBackground } from '../../summarize';
import type { ColorTarget } from '../../types';
import type { DividerCustomizations, TableCustomizations } from '@/components/preview/types';

const DEFAULT_TABLE_HEADER = '#e6e7e8';

interface ColorUsageListProps {
    localPrimary: string | undefined;
    localSecondary: string | undefined;
    tableConfig: TableCustomizations;
    updateTableConfig: (updates: Partial<TableCustomizations>) => void;
    dividerConfig: DividerCustomizations;
    updateDividerConfig: (updates: Partial<DividerCustomizations>) => void;
    /** Set when the capa carries its own colours, which override the palette. */
    coverOverridesColors: boolean;
    /** Which usage row is expanded; also drives what the preview shows. */
    target: ColorTarget;
    onTargetChange: (target: ColorTarget) => void;
}

function Swatch({ color, className }: { color: string | undefined; className?: string }) {
    return (
        <span
            className={cn('size-4 flex-shrink-0 rounded border border-border-strong', className)}
            style={color ? { backgroundColor: color } : undefined}
        />
    );
}

function Tile({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'flex flex-col items-center justify-center gap-2 rounded-lg border-2 p-3 transition-all',
                active
                    ? 'border-[#00006B] bg-[#00006B] text-white'
                    : 'border-border bg-card text-foreground hover:border-border-strong',
            )}
        >
            {children}
        </button>
    );
}

function HexInput({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
    return (
        <div className="mt-2 flex items-center gap-2">
            <div
                className="size-10 rounded border border-border"
                style={{ backgroundColor: value }}
            />
            <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-10 flex-1 cursor-pointer rounded"
            />
        </div>
    );
}

function UsageRow({
    label,
    value,
    swatches,
    expandable = false,
    expanded = false,
    onToggle,
    testId,
    children,
}: {
    label: string;
    value: string;
    /** One colour, or the whole palette for the rows that inherit it. */
    swatches?: Array<string | undefined>;
    expandable?: boolean;
    expanded?: boolean;
    onToggle?: () => void;
    /** Só nas linhas expansíveis: os tiles de dentro repetem rótulo entre
     * linhas ("Cor primária" existe no divider e na tabela), então o teste
     * precisa de um escopo por linha. */
    testId?: string;
    children?: React.ReactNode;
}) {
    const header = (
        <>
            <span
                className={cn(
                    'flex-1 text-left text-xs font-medium',
                    expandable ? 'text-foreground' : 'text-muted-foreground',
                )}
            >
                {label}
            </span>
            {swatches?.length ? (
                <span className="flex gap-1">
                    {swatches.map((color, index) => (
                        <Swatch key={`${color ?? 'none'}-${index}`} color={color} />
                    ))}
                </span>
            ) : null}
            <span className="text-xs text-muted-foreground">{value}</span>
            {/* The chevron is the only cue that a row can be acted on, so the rows
                that merely follow the palette hold its width instead of dropping
                it — otherwise their value column ends on a different edge. */}
            {expandable ? (
                <ArrowDown2
                    className={cn(
                        'size-4 flex-shrink-0 text-muted-foreground transition-transform',
                        expanded && 'rotate-180',
                    )}
                />
            ) : (
                <span aria-hidden className="size-4 flex-shrink-0" />
            )}
        </>
    );

    return (
        <div className="border-b border-border last:border-b-0" data-testid={testId}>
            {expandable ? (
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={expanded}
                    className={cn(
                        'flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 transition-colors hover:bg-surface',
                        expanded && 'bg-surface',
                    )}
                >
                    {header}
                </button>
            ) : (
                <div className="flex w-full items-center gap-2 px-3 py-2.5">{header}</div>
            )}
            {expandable && expanded ? <div className="space-y-3 px-3 pb-3">{children}</div> : null}
        </div>
    );
}

/**
 * Every colour the laudo paints, gathered under the palette that feeds them.
 * The controls for the divider and the table header used to live in their own
 * sections; only their home moved — they still write `customizations.divider`
 * and `customizations.table`.
 */
export function ColorUsageList({
    localPrimary,
    localSecondary,
    tableConfig,
    updateTableConfig,
    dividerConfig,
    updateDividerConfig,
    coverOverridesColors,
    target,
    onTargetChange,
}: ColorUsageListProps) {
    // Resolved, not raw: an absent `background_color` renders as the report
    // colour, so reading the raw field would light up the wrong tile and paint
    // the wrong swatch. Transparent is the empty string — `undefined` would be
    // swallowed by the `?? 'primary'` every renderer applies.
    const dividerBg = resolveDividerBackground(dividerConfig);
    const dividerIsCustom = !!dividerBg && dividerBg !== 'primary';
    const dividerValue = !dividerBg
        ? 'Transparente'
        : dividerBg === 'primary'
          ? 'Cor primária'
          : dividerBg.toUpperCase();

    // Mesma leitura resolvida do divider: `'primary'` é sentinela, não hex, e
    // só o campo cru distingue "acompanha a primária" de uma cópia congelada
    // dela.
    const tableHeader = tableConfig.header_color;
    const tableIsCustom = !!tableHeader && tableHeader !== 'primary';
    const tableValue = !tableHeader
        ? 'Cinza padrão'
        : tableHeader === 'primary'
          ? 'Cor primária'
          : tableHeader.toUpperCase();

    const toggle = (next: NonNullable<ColorTarget>) =>
        onTargetChange(target === next ? null : next);

    return (
        <div className="space-y-2">
            <Label className="text-sm font-medium">Onde estas cores aparecem</Label>
            <div className="rounded-lg border border-border" data-testid="color-usage-list">
                <UsageRow label="Títulos de seção" value="Cor primária" swatches={[localPrimary]} />

                <UsageRow
                    label="Títulos de grupo"
                    testId="color-usage-row-divider"
                    value={dividerValue}
                    swatches={[dividerBg === 'primary' ? localPrimary : (dividerBg ?? undefined)]}
                    expandable
                    expanded={target === 'divider'}
                    onToggle={() => toggle('divider')}
                >
                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Cor de fundo</Label>
                        <div className="grid grid-cols-3 gap-2">
                            <Tile
                                active={!dividerBg}
                                onClick={() =>
                                    updateDividerConfig({
                                        background_color: '',
                                        font_color: undefined,
                                    })
                                }
                            >
                                <Slash className="size-4" />
                                <span className="text-xs font-medium">Transparente</span>
                            </Tile>
                            <Tile
                                active={dividerBg === 'primary'}
                                onClick={() => updateDividerConfig({ background_color: 'primary' })}
                            >
                                <Swatch color={localPrimary} />
                                <span className="text-xs font-medium">Cor primária</span>
                            </Tile>
                            <Tile
                                active={dividerIsCustom}
                                onClick={() =>
                                    updateDividerConfig({
                                        background_color: localPrimary || DEFAULT_TABLE_HEADER,
                                    })
                                }
                            >
                                <Colorfilter className="size-4" />
                                <span className="text-xs font-medium">Personalizada</span>
                            </Tile>
                        </div>
                        {dividerIsCustom && (
                            <HexInput
                                value={dividerBg}
                                onChange={(hex) => updateDividerConfig({ background_color: hex })}
                            />
                        )}
                    </div>

                    {dividerBg && (
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Cor do texto</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <Tile
                                    active={!dividerConfig.font_color}
                                    onClick={() => updateDividerConfig({ font_color: undefined })}
                                >
                                    <span className="text-xs font-medium">Automático</span>
                                </Tile>
                                <Tile
                                    active={!!dividerConfig.font_color}
                                    onClick={() =>
                                        updateDividerConfig({
                                            font_color: dividerConfig.font_color || '#000000',
                                        })
                                    }
                                >
                                    <span className="text-xs font-medium">Personalizada</span>
                                </Tile>
                            </div>
                            {dividerConfig.font_color && (
                                <HexInput
                                    value={dividerConfig.font_color}
                                    onChange={(hex) => updateDividerConfig({ font_color: hex })}
                                />
                            )}
                        </div>
                    )}
                </UsageRow>

                <UsageRow
                    label="Cabeçalho de tabela"
                    testId="color-usage-row-table"
                    value={tableValue}
                    swatches={[
                        tableHeader === 'primary'
                            ? localPrimary || DEFAULT_TABLE_HEADER
                            : tableHeader || DEFAULT_TABLE_HEADER,
                    ]}
                    expandable
                    expanded={target === 'table'}
                    onToggle={() => toggle('table')}
                >
                    <div className="grid grid-cols-3 gap-2">
                        <Tile
                            active={!tableHeader}
                            onClick={() => updateTableConfig({ header_color: undefined })}
                        >
                            <Swatch color={DEFAULT_TABLE_HEADER} />
                            <span className="text-xs font-medium">Cinza padrão</span>
                        </Tile>
                        <Tile
                            active={tableHeader === 'primary'}
                            onClick={() => updateTableConfig({ header_color: 'primary' })}
                        >
                            <Swatch color={localPrimary} />
                            <span className="text-xs font-medium">Cor primária</span>
                        </Tile>
                        <Tile
                            active={tableIsCustom}
                            onClick={() =>
                                updateTableConfig({
                                    header_color: tableIsCustom
                                        ? tableHeader
                                        : localPrimary || DEFAULT_TABLE_HEADER,
                                })
                            }
                        >
                            <Colorfilter className="size-4" />
                            <span className="text-xs font-medium">Personalizada</span>
                        </Tile>
                    </div>
                    {tableIsCustom && (
                        <HexInput
                            value={tableHeader!}
                            onChange={(hex) => updateTableConfig({ header_color: hex })}
                        />
                    )}
                </UsageRow>

                <UsageRow
                    label="Gráficos"
                    value="Herda a paleta"
                    swatches={[localPrimary, localSecondary]}
                />
                <UsageRow
                    label="Capa"
                    value={coverOverridesColors ? 'Definida na capa' : 'Herda a paleta'}
                    swatches={coverOverridesColors ? undefined : [localPrimary, localSecondary]}
                />
            </div>
        </div>
    );
}
